import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError } from '../../api/admin-client.js';
import {
  activateAdminCategory,
  createAdminCategory,
  deactivateAdminCategory,
  fetchAdminCategories,
  updateAdminCategory,
  type CreateCategoryInput,
  type UpdateCategoryInput,
} from '../api/catalog-api.js';

const CATEGORIES_KEY = ['admin', 'catalog', 'categories'] as const;

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

export function useAdminCategories(params: { parentId?: string; isActive?: boolean } = {}) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: [...CATEGORIES_KEY, params],
    queryFn: async ({ signal }) =>
      fetchAdminCategories({ ...params, limit: 100, offset: 0 }, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 30_000,
  });
}

export function useAdminCategoryMutations() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });

  const create = useMutation({
    mutationFn: async (input: CreateCategoryInput) =>
      createAdminCategory(input, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateCategoryInput }) =>
      updateAdminCategory(id, input, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const activate = useMutation({
    mutationFn: async (id: string) => activateAdminCategory(id, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const deactivate = useMutation({
    mutationFn: async (id: string) => deactivateAdminCategory(id, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  return { create, update, activate, deactivate };
}
